import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Stat, Select, Button, Note, Quiet, NotRunning, C, when, errText, plural } from "./ui.jsx";
import { count } from "./reportsView.js";

/**
 * Do Not Call (TCPA) (the schematic's Settings · Do Not Call board): who asked not to be called, the
 * calls the list and the calling hours held back, and who agreed to be called.
 *
 * ⛔ IT CLAIMS ONLY WHAT THIS APP KNOWS. A driver whose consent was taken away and an applicant marked
 * do-not-contact are here. Someone who says "stop calling me" ON A CALL goes on the voice platform's
 * own list, which every call checks first (core freightCalls.js, fail closed), but that list is not
 * read back into this page yet: that row is marked "Not running yet", never counted as zero.
 */

const PERIODS = [{ v: "this_month", label: "This month" }, { v: "last_month", label: "Last month" }, { v: "12m", label: "Last 12 months" }];

/**
 * Save the server's CSV as a file (it needs the sign-in, so it cannot be a plain link). ⛔ The UTF-8
 * mark goes back on here (review): reading the answer as text drops the server's, and without it
 * Excel shows names with accents as garbage.
 */
export const csvBlobParts = (text) => (String(text).startsWith("\ufeff") ? [text] : ["\ufeff", text]);
function saveCsv(text, name) {
  try {
    const url = URL.createObjectURL(new Blob(csvBlobParts(text), { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { /* the page says the download did not work */ }
}

export function DoNotCallView({ company, stage, period, setPeriod, data, error, onDownload, busy, msg }) {
  const t = data?.tiles;
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title sub="Who is not to be called, and the calls held back here.">Do Not Call (TCPA)</Title>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Select id="dnc-period" label="Period" value={period} onChange={setPeriod} options={PERIODS} width={240} />
        <Button kind="secondary" disabled={busy || !data} onClick={onDownload} style={{ minHeight: 48 }}>Download report</Button>
      </div>
      <Note msg={msg} />
      {error && <Quiet>{`The numbers could not be read: ${error}`}</Quiet>}
      {!data && !error && <Quiet>Loading…</Quiet>}
      {t && (
        <>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Stat label="Marked do not call here" value={count(t.asked)} sub="A driver or an applicant" />
            <Stat label="Calls stopped by your list" value={count(t.stopped)} />
            <Stat label="Outside calling hours" value={count(t.held)} sub="Calls held back, where the person is" />
            <Stat label="Drivers who agreed to calls" value={`${count(t.agreed.yes)} of ${count(t.agreed.of)}`}
              sub={t.agreed.of - t.agreed.yes > 0 ? `The other ${t.agreed.of - t.agreed.yes} ${plural(t.agreed.of - t.agreed.yes, "isn't", "aren't")} called until they agree` : null} />
          </div>
          <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Marked do not call here</h2>
            {data.people.length === 0 && <Quiet>Nobody in this period.</Quiet>}
            {data.people.map((p, i) => (
              <div key={`${p.kind}-${p.id}`} style={{ display: "flex", gap: "4px 16px", flexWrap: "wrap", alignItems: "baseline", paddingTop: i ? 12 : 0, borderTop: i ? `1px solid ${C.soft}` : "none" }}>
                <span style={{ minWidth: 180, display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 17, fontWeight: 600 }}>{p.name}</span>
                  <span style={{ fontSize: 14, color: C.muted }}>{p.kind === "driver" ? "Driver" : "Applicant"}</span>
                </span>
                <span style={{ flex: "1 1 300px", fontSize: 16 }}>
                  {p.kind === "driver" ? "Consent to Alfred's calls taken away" : "Marked do not contact"}
                  <span style={{ display: "block", fontSize: 14, color: C.muted }}>{[when(p.at), p.by ? `by ${p.by}` : null].filter(Boolean).join(" · ")}</span>
                </span>
                <Link to={p.kind === "driver" ? `/carrier/dispatch/drivers/${p.id}` : `/carrier/hiring/${p.id}`} style={{ fontSize: 15, fontWeight: 600, textDecoration: "none" }}>Open</Link>
              </div>
            ))}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderTop: `1px solid ${C.soft}`, paddingTop: 12 }}>
              <span style={{ fontSize: 16 }}>Asked on a call</span>
              <NotRunning>Not shown here yet</NotRunning>
            </div>
          </Card>
          <p style={{ margin: 0, fontSize: 15, color: C.muted }}>Alfred checks the Do Not Call list before every call, and only calls between 8 AM and 9 PM, both at your office and where the person is.</p>
        </>
      )}
    </Shell>
  );
}

export default function DoNotCall() {
  const { carrier } = useCarrier();
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    freight.dncReport(period).then((d) => alive && setData(d)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [period]);
  const onDownload = async () => {
    setBusy(true); setMsg(null);
    try { saveCsv(await freight.dncReportCsv(period), `do-not-call-${period}.csv`); }
    catch (e) { setMsg({ ok: false, text: `The download did not work: ${errText(e)}` }); }
    finally { setBusy(false); }
  };
  return <DoNotCallView company={carrier.name} stage={carrier.stage} period={period} setPeriod={setPeriod} data={data} error={error} onDownload={onDownload} busy={busy} msg={msg} />;
}
